import React, { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import Labels from "../../services/apiLabels";
import {
  authHeaders,
  isApiSuccess,
  listFromPayload,
  officeId,
  pick,
  readJson,
} from "../Offices/officeHelpers";
import {
  matchesUserSearch,
  officeLabel,
  userId,
  userImageUrl,
  usersFromPayload,
} from "./inspectionUserHelpers";
import "./inspectionUsers.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const OFFICES_PATH = "/dealers/dashbord/offices";

function InspectionUserList() {
  const history = useHistory();
  const location = useLocation();
  const { officeId: officeIdParam } = useParams();
  const auth = getAuth();
  const listPath = `${OFFICES_PATH}/${officeIdParam}/users`;

  const [office, setOffice] = useState(location.state?.office || null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({
    type: location.state?.notice ? "success" : "",
    text: location.state?.notice || "",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadOffice = async () => {
    if (
      location.state?.office &&
      String(officeId(location.state.office)) === String(officeIdParam)
    ) {
      setOffice(location.state.office);
      return;
    }
    if (!auth?.token || !officeIdParam) return;
    try {
      const response = await fetch(API_ENDPOINT(Labels.office_listing), {
        headers: authHeaders(auth.token),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) return;
      const match = listFromPayload(json).find(
        (item) => String(officeId(item)) === String(officeIdParam)
      );
      if (match) setOffice(match);
    } catch {
      /* keep the generic heading */
    }
  };

  const loadUsers = async () => {
    if (!auth?.token || !officeIdParam) {
      setLoading(false);
      setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const url = `${API_ENDPOINT(
        Labels.inspection_office_users
      )}?office=${encodeURIComponent(officeIdParam)}`;
      const response = await fetch(url, {
        headers: authHeaders(auth.token),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) throw new Error("list failed");
      setUsers(usersFromPayload(json));
    } catch {
      setUsers([]);
      setBanner((prev) =>
        prev.type === "success"
          ? prev
          : {
              type: "error",
              text: "Could not load inspection users. Please try again.",
            }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffice();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeIdParam]);

  useEffect(() => {
    if (!location.state?.notice) return undefined;
    history.replace({ pathname: listPath, state: { office } });
    return undefined;
  }, [history, listPath, location.state, office]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const filtered = useMemo(
    () => users.filter((user) => matchesUserSearch(user, search)),
    [users, search]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);
  const isLoading = Boolean(loading) && users.length === 0;
  const heading = officeLabel(office);

  const confirmDelete = async () => {
    const id = userId(deleteTarget);
    if (!id || !auth?.token) return;
    setDeleting(true);
    try {
      const response = await fetch(
        API_ENDPOINT(`${Labels.delete_inspection_users}${id}/`),
        {
          method: "DELETE",
          headers: authHeaders(auth.token),
        }
      );
      if (!response.ok && response.status !== 204) {
        throw new Error("delete failed");
      }
      setDeleteTarget(null);
      setBanner({ type: "success", text: "Inspection user removed." });
      await loadUsers();
    } catch {
      setBanner({
        type: "error",
        text: "Could not delete this user. Please try again.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const goNew = () =>
    history.push({
      pathname: `${listPath}/new`,
      state: { office },
    });

  return (
    <div className="dd-section dd-offices dd-insp-users">
      {banner.text ? (
        <p
          className={
            banner.type === "success"
              ? "dd-password__hint"
              : "dd-password__error"
          }
          role="status"
        >
          {banner.text}
        </p>
      ) : null}

      <div className="dd-toolbar">
        <label className="dd-toolbar__search">
          <FiSearch size={16} aria-hidden />
          <input
            type="search"
            placeholder="Search name, email, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="dd-toolbar__clear-input"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          ) : null}
        </label>

        <label className="dd-toolbar__field">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <label className="dd-toolbar__field">
          <span>List</span>
          <button
            type="button"
            className="dd-toolbar__action-btn"
            onClick={loadUsers}
            disabled={loading}
          >
            Refresh
          </button>
        </label>

        <label className="dd-toolbar__field">
          <span>User</span>
          <button
            type="button"
            className="dd-btn dd-btn--primary"
            onClick={goNew}
          >
            <FiPlus size={16} aria-hidden />
            Add user
          </button>
        </label>
      </div>

      <div className="dd-toolbar__meta">
        {filtered.length ? (
          <span>
            Showing {pageStart + 1}–{pageEnd} of {filtered.length} at {heading}
            {filtered.length !== users.length
              ? ` (filtered from ${users.length})`
              : ""}
          </span>
        ) : (
          <span>No inspection users at {heading}</span>
        )}
      </div>

      {isLoading ? (
        <div className="dd-empty">
          <strong>Loading inspection users…</strong>
        </div>
      ) : !users.length ? (
        <div className="dd-empty">
          <strong>No inspection users yet</strong>
          <p>Create a login for staff at this inspection centre.</p>
          <button
            type="button"
            className="dd-btn dd-btn--primary"
            style={{ marginTop: "0.75rem" }}
            onClick={goNew}
          >
            <FiPlus size={16} aria-hidden />
            Add user
          </button>
        </div>
      ) : !filtered.length ? (
        <div className="dd-empty">
          <strong>No matches</strong>
          <p>Try a different search.</p>
        </div>
      ) : (
        <>
          <div className="dd-table-wrap">
            <table className="dd-table">
              <thead>
                <tr>
                  <th scope="col">User</th>
                  <th scope="col">Email</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Address</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((user, index) => {
                  const id = userId(user);
                  const image = userImageUrl(user);
                  const name = pick(user, ["name", "Name", "full_name"], "—");
                  return (
                    <tr key={id || `user-${index}`}>
                      <td>
                        <div className="dd-insp-users__identity">
                          {image ? (
                            <img
                              src={image}
                              alt=""
                              className="dd-insp-users__avatar"
                            />
                          ) : (
                            <span
                              className="dd-insp-users__avatar"
                              aria-hidden
                            >
                              {String(name).charAt(0).toUpperCase() || "?"}
                            </span>
                          )}
                          <span>{name}</span>
                        </div>
                      </td>
                      <td>{pick(user, ["email", "Email"], "—")}</td>
                      <td>
                        {pick(user, ["phone_number", "phone", "mobile"], "—")}
                      </td>
                      <td>{pick(user, ["address", "Address"], "—")}</td>
                      <td>
                        <div className="dd-offices__actions">
                          <button
                            type="button"
                            className="dd-btn dd-btn--ghost"
                            disabled={!id}
                            onClick={() =>
                              history.push({
                                pathname: `${listPath}/${id}/edit`,
                                state: { office, user },
                              })
                            }
                          >
                            <FiEdit2 size={14} aria-hidden />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="dd-btn dd-btn--danger"
                            onClick={() => setDeleteTarget(user)}
                          >
                            <FiTrash2 size={14} aria-hidden />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length > pageSize ? (
            <div className="dd-pagination">
              <PaginationControl
                page={safePage}
                between={3}
                total={filtered.length}
                limit={pageSize}
                changePage={setPage}
                ellipsis={1}
              />
            </div>
          ) : null}
        </>
      )}

      <div className="dd-btn-row">
        <button
          type="button"
          className="dd-btn dd-btn--ghost"
          onClick={() => history.push(OFFICES_PATH)}
        >
          <FiArrowLeft size={16} aria-hidden />
          Back to offices
        </button>
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this inspection user?"
        message={
          deleteTarget
            ? `Remove ${pick(
                deleteTarget,
                ["name", "Name", "email"],
                "this user"
              )}? They will no longer be able to sign in for inspections.`
            : ""
        }
        confirmLabel="Delete user"
        confirming={deleting}
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default InspectionUserList;
